/**
 * Shared safety module - Report & Block UI
 * Used by profile-view and chat views.
 */
import { showToast, showApiError } from '../main';
import { blocksApi, reportsApi, adminApi } from '../api';
import {
  TOAST_REPORT_SUBMITTED,
  TOAST_FAILED_LOAD_REPORT_CATS,
  toastBlocked,
  toastAdminDeleted,
  API_ERR_SUBMIT_REPORT,
  API_ERR_BLOCK_USER,
  API_ERR_ADMIN_DELETE,
} from './app-strings';

let cachedReportCategories: string[] | null = null;

/** Bottom action sheet with Report / Block / Delete (admin) / Cancel */
export function showSafetyActionSheet(
  userId: string,
  firstName: string,
  options?: { onBlocked?: () => void; onDeleted?: () => void; isSelf?: boolean; isAdmin?: boolean }
): void {
  const isSelf = options?.isSelf ?? false;
  const isAdmin = options?.isAdmin ?? false;
  const showDelete = isAdmin && !isSelf;

  const html = `
    <div class="action-sheet-overlay" id="action-sheet-overlay">
      <div class="action-sheet">
        <div class="action-sheet-header">${firstName}</div>
        <button class="action-sheet-item${isSelf ? ' action-sheet-disabled' : ''}" id="action-report"${isSelf ? ' disabled' : ''}>
          <i class="fas fa-flag"></i> Report ${firstName}
        </button>
        ${isSelf ? '<span class="action-sheet-hint">Can\'t report yourself</span>' : ''}
        <button class="action-sheet-item action-sheet-danger${isSelf ? ' action-sheet-disabled' : ''}" id="action-block"${isSelf ? ' disabled' : ''}>
          <i class="fas fa-ban"></i> Block ${firstName}
        </button>
        ${isSelf ? '<span class="action-sheet-hint">Can\'t block yourself</span>' : ''}
        ${showDelete ? `
        <button class="action-sheet-item action-sheet-danger" id="action-admin-delete">
          <i class="fas fa-trash"></i> Delete User
        </button>
        ` : ''}
        <button class="action-sheet-item action-sheet-cancel" id="action-cancel">
          Cancel
        </button>
      </div>
    </div>
  `;

  document.body.insertAdjacentHTML('beforeend', html);

  if (!isSelf) {
    document.getElementById('action-report')?.addEventListener('click', () => {
      closeActionSheet();
      showReportModal(userId, firstName);
    });

    document.getElementById('action-block')?.addEventListener('click', () => {
      closeActionSheet();
      showBlockConfirmModal(userId, firstName, options?.onBlocked);
    });
  }

  if (showDelete) {
    document.getElementById('action-admin-delete')?.addEventListener('click', () => {
      closeActionSheet();
      showAdminDeleteConfirmModal(userId, firstName, options?.onDeleted);
    });
  }

  document.getElementById('action-cancel')?.addEventListener('click', closeActionSheet);

  document.getElementById('action-sheet-overlay')?.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).id === 'action-sheet-overlay') {
      closeActionSheet();
    }
  });
}

function closeActionSheet(): void {
  document.getElementById('action-sheet-overlay')?.remove();
}

async function showReportModal(userId: string, firstName: string): Promise<void> {
  if (!cachedReportCategories) {
    try {
      const response = await reportsApi.getCategories();
      cachedReportCategories = response.data;
    } catch {
      showToast(TOAST_FAILED_LOAD_REPORT_CATS, 'error');
      return;
    }
  }

  const categories = cachedReportCategories;

  const html = `
    <div class="modal-overlay" id="report-modal">
      <div class="modal-content">
        <h3>Report ${firstName}</h3>
        <p style="color: var(--text-secondary); margin-bottom: 0.75rem">Select a reason</p>
        <div class="radio-group single-column">
          ${categories.map(cat => `
            <label class="radio-item">
              <input type="radio" name="report-category" value="${cat}">
              <span class="radio-circle"></span>
              <span>${formatCategoryLabel(cat)}</span>
            </label>
          `).join('')}
        </div>
        <div class="input-group" style="margin-top: 0.75rem">
          <label>Details (optional)</label>
          <textarea class="input" id="report-description" rows="3" maxlength="500" placeholder="Add additional details..."></textarea>
        </div>
        <div class="modal-buttons">
          <button class="btn btn-secondary" id="report-cancel-btn">Cancel</button>
          <button class="btn btn-danger" id="report-submit-btn" disabled>Submit</button>
        </div>
      </div>
    </div>
  `;

  document.body.insertAdjacentHTML('beforeend', html);

  const radios = document.querySelectorAll('input[name="report-category"]');
  const submitBtn = document.getElementById('report-submit-btn') as HTMLButtonElement;

  radios.forEach(radio => {
    radio.addEventListener('change', () => {
      submitBtn.disabled = false;
    });
  });

  submitBtn?.addEventListener('click', async () => {
    const selected = document.querySelector('input[name="report-category"]:checked') as HTMLInputElement;
    if (!selected) return;

    submitBtn.disabled = true;

    try {
      const description = (document.getElementById('report-description') as HTMLTextAreaElement)?.value || undefined;
      await reportsApi.report(userId, selected.value, description);
      showToast(TOAST_REPORT_SUBMITTED, 'success');
      closeReportModal();
    } catch (err: any) {
      showApiError(err, API_ERR_SUBMIT_REPORT);
      submitBtn.disabled = false;
    }
  });

  document.getElementById('report-cancel-btn')?.addEventListener('click', closeReportModal);

  document.getElementById('report-modal')?.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).id === 'report-modal') {
      closeReportModal();
    }
  });
}

function closeReportModal(): void {
  document.getElementById('report-modal')?.remove();
}

async function showBlockConfirmModal(
  userId: string,
  firstName: string,
  onBlocked?: () => void
): Promise<void> {
  const html = `
    <div class="modal-overlay" id="block-modal">
      <div class="modal-content">
        <h3>Block ${firstName}?</h3>
        <p style="color: var(--text-secondary)">They won't be able to see your profile or contact you.</p>
        <div class="modal-buttons">
          <button class="btn btn-secondary" id="block-cancel-btn">Cancel</button>
          <button class="btn btn-danger" id="block-confirm-btn">Block</button>
        </div>
      </div>
    </div>
  `;

  document.body.insertAdjacentHTML('beforeend', html);

  const confirmBtn = document.getElementById('block-confirm-btn') as HTMLButtonElement;

  confirmBtn?.addEventListener('click', async () => {
    confirmBtn.disabled = true;

    try {
      await blocksApi.block(userId);
      showToast(toastBlocked(firstName), 'success');
      closeBlockModal();
      onBlocked?.();
    } catch (err: any) {
      showApiError(err, API_ERR_BLOCK_USER);
      confirmBtn.disabled = false;
    }
  });

  document.getElementById('block-cancel-btn')?.addEventListener('click', closeBlockModal);

  document.getElementById('block-modal')?.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).id === 'block-modal') {
      closeBlockModal();
    }
  });
}

function closeBlockModal(): void {
  document.getElementById('block-modal')?.remove();
}

async function showAdminDeleteConfirmModal(
  userId: string,
  firstName: string,
  onDeleted?: () => void
): Promise<void> {
  const html = `
    <div class="modal-overlay" id="admin-delete-modal">
      <div class="modal-content">
        <h3>Delete ${firstName}?</h3>
        <p style="color: var(--text-secondary)">Permanently delete ${firstName}? This cannot be undone.</p>
        <div class="modal-buttons">
          <button class="btn btn-secondary" id="admin-delete-cancel-btn">Cancel</button>
          <button class="btn btn-danger" id="admin-delete-confirm-btn">Delete</button>
        </div>
      </div>
    </div>
  `;

  document.body.insertAdjacentHTML('beforeend', html);

  const confirmBtn = document.getElementById('admin-delete-confirm-btn') as HTMLButtonElement;

  confirmBtn?.addEventListener('click', async () => {
    confirmBtn.disabled = true;

    try {
      await adminApi.deleteUser(userId);
      showToast(toastAdminDeleted(firstName), 'success');
      closeAdminDeleteModal();
      onDeleted?.();
    } catch (err: any) {
      showApiError(err, API_ERR_ADMIN_DELETE);
      confirmBtn.disabled = false;
    }
  });

  document.getElementById('admin-delete-cancel-btn')?.addEventListener('click', closeAdminDeleteModal);

  document.getElementById('admin-delete-modal')?.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).id === 'admin-delete-modal') {
      closeAdminDeleteModal();
    }
  });
}

function closeAdminDeleteModal(): void {
  document.getElementById('admin-delete-modal')?.remove();
}

function formatCategoryLabel(category: string): string {
  return category.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}
